import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { api } from '../api';
import {
  ApiError,
  SUPORTE_ASSUNTOS,
  type SuporteAssunto,
  type SuporteChamado,
  type SuporteContexto,
  type SuporteFrequencia,
  type SuporteTipo
} from '../api/types';
import { idiomaAtual, t } from '../i18n/idioma';
import { useIdioma } from '../i18n/useIdioma';
import { useFechar } from '../input/useAtalhos';
import { aoAvisoDeSuporte } from '../suporte/aviso';
import { capturarMundo, montarContexto, navegador, VERSAO_FRONT, type DadosDoJogo } from '../suporte/coleta';
import { Chave } from './hud/Cartao';

/*
  Suporte: o jogador relata bug ou sugere melhoria (docs/prototipos/suporte-nota.md).
  A equipe não responde um a um: o jogador vê só a situação, e a equipe fala com ele apenas
  quando precisa de mais detalhes ("Precisa de você"). A regra aparece no formulário, no
  "recebido" e em Meus chamados.
*/

type Aba = 'novo' | 'meus';
type Campo = 'assunto' | 'titulo' | 'relato' | 'resposta' | null;

interface Props {
  onClose(): void;
  /** Lido na hora de abrir: a captura é a tela como estava. */
  canvas(): HTMLCanvasElement | null;
  jogo(): DadosDoJogo;
  personagem: string;
  shard: string;
  vip: boolean;
  abaInicial?: Aba;
  /** A lista mudou (enviou ou respondeu): a HUD recarrega o resumo. */
  onMudou(): void;
}

const TITULO_MAX = 80;
const RELATO_MAX = 2000;

const SVG: Record<string, ReactNode> = {
  bug: <><path d="M8 6a4 4 0 0 1 8 0" /><rect x="7" y="7" width="10" height="13" rx="5" /><path d="M12 11v9M3 13h4M17 13h4M4 7l3 2M20 7l-3 2M4 20l3-3M20 20l-3-3" /></>,
  sugestao: <><path d="M9 18h6M10 21h4" /><path d="M12 3a6 6 0 0 0-3.5 10.9c.6.4 1 1.1 1 1.9V16h5v-.2c0-.8.4-1.5 1-1.9A6 6 0 0 0 12 3z" /></>,
  novo: <path d="M12 5v14M5 12h14" />,
  lista: <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />,
  ok: <><circle cx="12" cy="12" r="9" /><path d="M8 12.5l2.6 2.6L16 9.5" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></>
};

function Ic({ k }: { k: keyof typeof SVG | string }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true">{SVG[k]}</svg>;
}

function usePuxarAviso(texto: string | null, pulso: number) {
  const ref = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (!texto) return;
    const el = ref.current;
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.focus({ preventScroll: true });
  }, [texto, pulso]);
  return ref;
}

function dataCurta(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(idiomaAtual(), { day: '2-digit', month: '2-digit' });
}

const TAG: Record<SuporteChamado['situacao'], string> = {
  recebido: 'aberto',
  analise: 'analise',
  contato: 'contato',
  analisado: 'ok'
};

export function SuporteModal({ onClose, canvas, jogo, personagem, shard, vip, abaInicial = 'novo', onMudou }: Props) {
  useIdioma();
  useFechar(onClose, { modal: true });

  const [aba, setAba] = useState<Aba>(abaInicial);
  const [tipo, setTipo] = useState<SuporteTipo>('bug');
  const [assunto, setAssunto] = useState<SuporteAssunto | ''>('');
  const [titulo, setTitulo] = useState('');
  const [relato, setRelato] = useState('');
  const [freq, setFreq] = useState<SuporteFrequencia>('sempre');
  const [comCaptura, setComCaptura] = useState(true);
  const [captura, setCaptura] = useState<{ url: string; base64: string } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [avisoPulso, setAvisoPulso] = useState(0);
  const [campo, setCampo] = useState<Campo>(null);
  const [busy, setBusy] = useState(false);
  const [enviado, setEnviado] = useState<number | null>(null);
  const [meus, setMeus] = useState<readonly SuporteChamado[] | null>(null);
  const [falhaLista, setFalhaLista] = useState(false);
  const [aberto, setAberto] = useState<number | null>(null);
  const [resposta, setResposta] = useState('');
  const [erroResposta, setErroResposta] = useState<string | null>(null);
  const [respostaPulso, setRespostaPulso] = useState(0);
  const avisoRef = usePuxarAviso(erro, avisoPulso);

  const dados = useRef<DadosDoJogo>(jogo());
  const contexto = useMemo<SuporteContexto>(() => montarContexto(dados.current), []);
  const nav = useMemo(() => navegador(), []);
  const primeiroTipo = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let vivo = true;
    void capturarMundo(canvas()).then(c => { if (vivo) setCaptura(c); });
    return () => { vivo = false; };
    // A captura é da tela no momento em que a janela abriu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregar = useCallback(() => {
    setFalhaLista(false);
    api.suporteMeus()
      .then(lista => {
        setMeus(lista);
        const pede = lista.find(c => c.situacao === 'contato');
        if (pede) setAberto(atual => atual ?? pede.numero);
      })
      .catch(() => setFalhaLista(true));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => aoAvisoDeSuporte(carregar), [carregar]);
  useEffect(() => { if (aba === 'novo' && !enviado) primeiroTipo.current?.focus(); }, [aba, enviado]);

  const falhar = (msg: string, alvo: Campo) => {
    setErro(msg);
    setCampo(alvo);
    setAvisoPulso(n => n + 1);
  };

  const andando = meus?.filter(c => c.situacao === 'recebido' || c.situacao === 'analise').length ?? 0;
  const pede = meus?.filter(c => c.situacao === 'contato').length ?? 0;

  const trocarTipo = (novo: SuporteTipo) => {
    setTipo(novo);
    setComCaptura(novo === 'bug');
  };

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setErro(null);
    setCampo(null);
    if (!assunto) return falhar(t('suporte.assunto'), 'assunto');
    if (titulo.trim().length < 6) return falhar(t('suporte.titulo'), 'titulo');
    if (relato.trim().length < 20) return falhar(t('suporte.relato'), 'relato');
    setBusy(true);
    try {
      const r = await api.suporteAbrir({
        tipo,
        assunto,
        titulo: titulo.trim(),
        relato: relato.trim(),
        frequencia: tipo === 'bug' ? freq : null,
        plataforma: dados.current.modo,
        idioma: idiomaAtual(),
        versaoFront: VERSAO_FRONT,
        versaoMundo: dados.current.mundo,
        contexto,
        captura: comCaptura && captura ? captura.base64 : null
      });
      setEnviado(r.numero);
      setTitulo('');
      setRelato('');
      setAssunto('');
      carregar();
      onMudou();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : t('suporte.naoEnviou');
      const codigo = err instanceof ApiError ? err.code : '';
      const alvo: Campo = codigo === 'suporte.assunto' ? 'assunto'
        : codigo === 'suporte.titulo' ? 'titulo'
        : codigo === 'suporte.relato' ? 'relato'
        : null;
      falhar(msg, alvo);
    } finally {
      setBusy(false);
    }
  };

  const responder = async (numero: number) => {
    if (busy) return;
    if (resposta.trim().length < 10) {
      setErroResposta(t('suporte.resposta'));
      setCampo('resposta');
      setRespostaPulso(n => n + 1);
      return;
    }
    setBusy(true);
    setErroResposta(null);
    try {
      await api.suporteResponder(numero, resposta.trim());
      setResposta('');
      setCampo(null);
      carregar();
      onMudou();
    } catch (err) {
      setErroResposta(err instanceof ApiError ? err.message : t('suporte.naoEnviou'));
      setRespostaPulso(n => n + 1);
    } finally {
      setBusy(false);
    }
  };

  const linhasJunto: [string, [string, string][]][] = [
    [t('suporte.junto.voce'), [
      [t('suporte.junto.conta'), t('suporte.junto.contaValor')],
      [t('suporte.junto.personagem'), personagem],
      [t('suporte.junto.shard'), shard],
      [t('suporte.junto.vip'), vip ? t('suporte.sim') : t('suporte.nao')]
    ]],
    [t('suporte.junto.onde'), [
      [t('suporte.junto.lugar'), contexto.lugar ?? '-'],
      [t('suporte.junto.posicao'), contexto.x != null ? `${contexto.x}, ${contexto.y} · ${t('suporte.junto.canal', { n: contexto.canal ?? 0 })}` : '-'],
      [t('suporte.junto.cacada'), contexto.cacando ? t('suporte.junto.cacando') : t('suporte.junto.parada')],
      [t('suporte.junto.grupoDuelo'), [contexto.grupo ? t('suporte.junto.grupo') : '', contexto.duelo ? t('suporte.junto.duelo') : ''].filter(Boolean).join(' · ') || t('suporte.nao')]
    ]],
    [t('suporte.junto.aparelho'), [
      [t('suporte.junto.navegador'), nav.nome],
      [t('suporte.junto.sistema'), nav.sistema],
      [t('suporte.junto.telaJogo'), `${contexto.modo === 'cel' ? t('suporte.junto.celular') : contexto.modo === 'tablet' ? 'tablet' : 'PC'} · ${contexto.telaW}×${contexto.telaH} @${contexto.dpr}x`],
      [t('suporte.junto.telaAparelho'), `${contexto.aparelhoW}×${contexto.aparelhoH}`],
      [t('suporte.junto.toque'), contexto.toque ? t('suporte.sim') : t('suporte.nao')],
      [t('suporte.junto.idiomaFuso'), `${contexto.idioma ?? '?'} · ${contexto.fuso ?? '?'}`],
      [t('suporte.junto.memoria'), `${contexto.memoria ? `${contexto.memoria} GB` : '?'} · ${contexto.nucleos ?? '?'}`],
      [t('suporte.junto.gpu'), contexto.gpu ?? t('suporte.junto.naoInformado')]
    ]],
    [t('suporte.junto.conexao'), [
      [t('suporte.junto.latencia'), contexto.latMedia != null ? t('suporte.junto.latenciaValor', { media: contexto.latMedia, pior: contexto.latPior ?? contexto.latMedia }) : t('suporte.junto.naoInformado')],
      [t('suporte.junto.quedas'), String(contexto.quedas ?? 0)],
      [t('suporte.junto.rede'), contexto.rede ? `${contexto.rede}${contexto.downlink ? ` · ${contexto.downlink} Mb/s` : ''}` : t('suporte.junto.naoInformado')],
      [t('suporte.junto.fps'), contexto.fpsMedio != null ? t('suporte.junto.fpsValor', { media: contexto.fpsMedio, pior: contexto.fpsPior ?? contexto.fpsMedio }) : t('suporte.junto.naoInformado')],
      [t('suporte.junto.sessao'), t('suporte.junto.minutos', { n: contexto.sessaoMin ?? 0 })]
    ]],
    [t('suporte.junto.versoes'), [
      [t('suporte.junto.front'), VERSAO_FRONT],
      [t('suporte.junto.mundo'), contexto.mundo ?? '-']
    ]],
    [t('suporte.junto.erros'), (contexto.erros?.length ?? 0) === 0
      ? [[t('suporte.junto.errosScript'), t('suporte.junto.nenhumErro')]]
      : (contexto.erros ?? []).slice(-5).map((e, i) => [`#${i + 1}`, e] as [string, string])],
    [t('suporte.junto.diario'), (contexto.diario ?? []).length === 0
      ? [['-', t('suporte.junto.nada')]]
      : (contexto.diario ?? []).map(d => [d.t, d.m] as [string, string])]
  ];

  let painel: ReactNode;
  if (aba === 'meus') {
    painel = (
      <div className="stack">
        <Como />
        {falhaLista && <p className="notice" role="alert">{t('suporte.falhaLista')}</p>}
        {meus === null && !falhaLista && <p className="p-sup__vazio">{t('comum.carregando')}</p>}
        {meus !== null && meus.length === 0 && <p className="p-sup__vazio">{t('suporte.semChamados')}</p>}
        {meus !== null && meus.length > 0 && (
          <div className="p-sup__lista">
            {[...meus]
              .sort((a, b) => Number(b.situacao === 'contato') - Number(a.situacao === 'contato') || b.numero - a.numero)
              .map(c => {
                const estaAberto = aberto === c.numero;
                return (
                  <div className={`p-sup__ch${estaAberto ? ' is-aberto' : ''}`} key={c.numero}>
                    <button type="button" aria-expanded={estaAberto} onClick={() => setAberto(estaAberto ? null : c.numero)}>
                      <Ic k={c.tipo} />
                      <span>
                        <b>#{c.numero} · {c.titulo}</b>
                        <small>{t(c.tipo === 'bug' ? 'suporte.bug' : 'suporte.sugestao')} · {t(`suporte.assunto.${c.assunto}`)} · {dataCurta(c.criadoEm)}</small>
                      </span>
                      <span className={`p-tag p-tag--${TAG[c.situacao]}`}>{t(`suporte.situacao.${c.situacao}`)}</span>
                    </button>
                    {estaAberto && (
                      <Corpo
                        c={c}
                        busy={busy}
                        resposta={resposta}
                        ruim={campo === 'resposta'}
                        erro={erroResposta}
                        pulso={respostaPulso}
                        onResposta={v => { setResposta(v); if (campo === 'resposta') setCampo(null); }}
                        onResponder={() => void responder(c.numero)}
                      />
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>
    );
  } else if (enviado) {
    painel = (
      <div className="p-sup__ok" role="status">
        <Ic k="ok" />
        <h3>{t('suporte.recebidoTitulo', { n: enviado })}</h3>
        <p>{t('suporte.recebidoTexto')}</p>
        <p className="p-sup__aviso" style={{ textAlign: 'left' }}>
          <Ic k="info" />
          <span><b>{t('suporte.semRespostaEscrita')}</b> {t('suporte.semRespostaEscritaTexto')}</span>
        </p>
        <div className="p-sup__acoes">
          <button type="button" className="btn" onClick={() => setAba('meus')}>{t('suporte.verMeus')}</button>
          <button type="button" className="btn btn--ghost" onClick={() => { setEnviado(null); setErro(null); }}>{t('suporte.abrirOutro')}</button>
        </div>
      </div>
    );
  } else {
    const bug = tipo === 'bug';
    painel = (
      <form className="stack" id="fSuporte" noValidate onSubmit={e => void enviar(e)}>
        <p className="p-sup__aviso"><Ic k="info" /><span>{t('suporte.aviso1')} <b>{t('suporte.aviso2')}</b>{t('suporte.aviso3')}</span></p>
        <div className="p-sup__tipos" role="radiogroup" aria-label={t('suporte.oQueRelatar')}>
          <button ref={primeiroTipo} type="button" className="p-sup__tipo" role="radio" aria-checked={bug} onClick={() => trocarTipo('bug')}>
            <Ic k="bug" /><span><b>{t('suporte.bug')}</b><small>{t('suporte.bugDesc')}</small></span>
          </button>
          <button type="button" className="p-sup__tipo" role="radio" aria-checked={!bug} onClick={() => trocarTipo('sugestao')}>
            <Ic k="sugestao" /><span><b>{t('suporte.sugestao')}</b><small>{t('suporte.sugestaoDesc')}</small></span>
          </button>
        </div>
        <div className="p-sup__duas">
          <label className={`field${campo === 'assunto' ? ' is-ruim' : ''}`}>
            <span className="field__label">{t('suporte.campoAssunto')}</span>
            <select value={assunto} onChange={e => { setAssunto(e.target.value as SuporteAssunto); if (campo === 'assunto') setCampo(null); }} disabled={busy}>
              <option value="">{t('suporte.escolha')}</option>
              {SUPORTE_ASSUNTOS.map(a => <option key={a} value={a}>{t(`suporte.assunto.${a}`)}</option>)}
            </select>
          </label>
          <label className={`field${campo === 'titulo' ? ' is-ruim' : ''}`}>
            <span className="field__label">{t('suporte.campoTitulo')}</span>
            <input value={titulo} maxLength={TITULO_MAX} disabled={busy}
              placeholder={t(bug ? 'suporte.exTituloBug' : 'suporte.exTituloSugestao')}
              onChange={e => { setTitulo(e.target.value); if (campo === 'titulo') setCampo(null); }} />
          </label>
        </div>
        <label className={`field${campo === 'relato' ? ' is-ruim' : ''}`}>
          <span className="field__label">{t(bug ? 'suporte.campoRelatoBug' : 'suporte.campoRelatoSugestao')}</span>
          <textarea rows={5} value={relato} maxLength={RELATO_MAX} disabled={busy}
            placeholder={t(bug ? 'suporte.exRelatoBug' : 'suporte.exRelatoSugestao')}
            onChange={e => { setRelato(e.target.value); if (campo === 'relato') setCampo(null); }} />
        </label>
        <div className="p-sup__conta">{relato.length} / {RELATO_MAX}</div>
        {bug && (
          <div className="p-card">
            <div className="p-card__h"><div><h3>{t('suporte.frequencia')}</h3><p>{t('suporte.frequenciaDesc')}</p></div></div>
            <div className="p-seg" role="radiogroup" aria-label={t('suporte.frequencia')}>
              {(['sempre', 'as-vezes', 'uma-vez'] as const).map(f => (
                <button key={f} type="button" role="radio" aria-checked={freq === f} onClick={() => setFreq(f)}>{t(`suporte.freq.${f}`)}</button>
              ))}
            </div>
          </div>
        )}
        <div className="p-card">
          <div className={`p-sup__cap${comCaptura && captura ? '' : ' is-off'}`}>
            {captura ? <img src={captura.url} alt={t('suporte.capturaAlt')} /> : <span className="p-sup__semcap">{t('suporte.semCaptura')}</span>}
            <div className="p-opt">
              <span><b>{t('suporte.anexarCaptura')}</b><small>{t('suporte.capturaAjuda')}</small></span>
              {captura && <Chave on={comCaptura} onClick={() => setComCaptura(v => !v)} />}
            </div>
          </div>
        </div>
        {erro && <p ref={avisoRef} className="notice" role="alert" tabIndex={-1}>{erro}</p>}
        <details className="p-sup__junto">
          <summary><span><b>{t('suporte.oQueVai')}</b> · {t('suporte.oQueVaiDesc')}</span></summary>
          <div className="p-sup__grupos">
            {linhasJunto.map(([g, linhas]) => (
              <div key={g}>
                <h4>{g}</h4>
                <dl className="p-sup__dl">
                  {linhas.map(([k, v], i) => <div key={i} style={{ display: 'contents' }}><dt>{k}</dt><dd>{v}</dd></div>)}
                </dl>
              </div>
            ))}
            <p className="p-sup__nao"><b>{t('suporte.naoVao')}</b> {t('suporte.naoVaoTexto')}</p>
          </div>
        </details>
      </form>
    );
  }

  const nav2: [Aba, string, string, ReactNode][] = [
    ['novo', t('suporte.novo'), t('suporte.novoDesc'), null],
    ['meus', t('suporte.meus'), '', pede > 0
      ? <span className="p-sup__chama">{t('suporte.precisamDeVoce', { n: pede })}</span>
      : t('suporte.emAndamento', { n: andando })]
  ];

  return (
    <div className="modal" role="presentation" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={t('suporte.janela')} className="p-cfg p-sup" onClick={e => e.stopPropagation()}>
        <section className="gump">
          <header className="gump__head">
            <h2 className="gump__title">{t('suporte.janela')}</h2>
            <button type="button" className="btn btn--tiny btn--ghost win__close" onClick={onClose} aria-label={t('comum.fechar')}>×</button>
          </header>
          <div className="gump__body p-cfg__corpo">
            <nav className="p-cfg__nav" role="tablist" aria-label={t('suporte.secoes')}>
              {nav2.map(([id, nome, desc, extra]) => (
                <button type="button" role="tab" aria-selected={aba === id} key={id} onClick={() => { setAba(id); if (id === 'meus') carregar(); }}>
                  <Ic k={id === 'novo' ? 'novo' : 'lista'} />
                  <span><b>{nome}</b><small>{extra ?? desc}</small></span>
                </button>
              ))}
            </nav>
            <div className="p-cfg__painel" role="tabpanel">{painel}</div>
          </div>
          {aba === 'novo' && !enviado ? (
            <footer className="p-cfg__pe">
              <span>{t('suporte.limiteHora')}</span>
              <button type="submit" form="fSuporte" className="btn btn--primary" disabled={busy}>
                {busy ? t('comum.enviando') : t('suporte.enviar')}
              </button>
            </footer>
          ) : (
            <footer className="p-cfg__pe"><span /><button type="button" className="btn btn--tiny win__close" onClick={onClose}>{t('comum.fechar')}</button></footer>
          )}
        </section>
      </div>
    </div>
  );
}

function Como() {
  return (
    <div className="p-sup__como">
      <ol className="p-sup__fluxo" aria-label={t('suporte.comoAnda')}>
        <li><b>{t('suporte.situacao.recebido')}</b><small>{t('suporte.passo.recebido')}</small></li>
        <li><b>{t('suporte.situacao.analise')}</b><small>{t('suporte.passo.analise')}</small></li>
        <li><b>{t('suporte.situacao.analisado')}</b><small>{t('suporte.passo.analisado')}</small></li>
      </ol>
      <p>
        {t('suporte.aviso1')} <b>{t('suporte.aviso2')}</b>{t('suporte.comoTexto1')}{' '}
        <span className="p-tag p-tag--contato">{t('suporte.situacao.contato')}</span>{' '}
        {t('suporte.comoTexto2')}
      </p>
    </div>
  );
}

const ORDEM: Record<SuporteChamado['situacao'], number> = { recebido: 0, analise: 1, contato: 1, analisado: 2 };

function Corpo({
  c, busy, resposta, ruim, erro, pulso, onResposta, onResponder
}: {
  c: SuporteChamado;
  busy: boolean;
  resposta: string;
  ruim: boolean;
  erro: string | null;
  pulso: number;
  onResposta(v: string): void;
  onResponder(): void;
}) {
  const avisoRef = usePuxarAviso(erro, pulso);
  const n = ORDEM[c.situacao];
  const datas: Record<string, string | null> = { recebido: c.criadoEm, analise: c.analiseEm, analisado: c.finalizadoEm };
  const passos = (['recebido', 'analise', 'analisado'] as const).map((k, i) => {
    const cls = i < n || (i === n && c.situacao === 'analisado') ? 'is-feito' : i === n ? 'is-agora' : '';
    const d = dataCurta(datas[k]);
    return <li key={k} className={cls}><i />{t(`suporte.situacao.${k}`)}{d && <> <time>{d}</time></>}</li>;
  });
  const conversa = c.conversa.map((m, i) => {
    const ultimoPedido = c.situacao === 'contato' && m.de === 'equipe' && i === c.conversa.length - 1;
    if (ultimoPedido) return null;
    return (
      <div key={i} className={`p-sup__resp ${m.de === 'equipe' ? 'p-sup__resp--pede' : 'p-sup__resp--jog'}`}>
        <b>{t(m.de === 'equipe' ? 'suporte.equipePediu' : 'suporte.voceRespondeu')}</b>
        {m.texto}
        <small>{dataCurta(m.em)}</small>
      </div>
    );
  });
  const pedido = c.situacao === 'contato' ? [...c.conversa].reverse().find(m => m.de === 'equipe') : undefined;
  return (
    <div className="p-sup__ch-corpo">
      <ol className="p-sup__passos" aria-label={t('suporte.situacaoAria')}>{passos}</ol>
      <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{c.relato}</p>
      {conversa}
      {c.situacao === 'contato' && pedido && (
        <>
          <div className="p-sup__resp p-sup__resp--pede">
            <b>{t('suporte.equipePrecisa')}</b>
            {pedido.texto}
            <small>{t('suporte.pedidoEm', { data: dataCurta(pedido.em) })}</small>
          </div>
          <div className="p-sup__responder">
            <label className={`field${ruim ? ' is-ruim' : ''}`}>
              <span className="field__label">{t('suporte.suaResposta')}</span>
              <textarea rows={3} maxLength={1000} value={resposta} disabled={busy}
                placeholder={t('suporte.suaRespostaEx')} onChange={e => onResposta(e.target.value)} />
            </label>
            {erro && <p ref={avisoRef} className="notice" role="alert" tabIndex={-1}>{erro}</p>}
            <button type="button" className="btn btn--primary" disabled={busy} onClick={onResponder}>{t('suporte.enviarResposta')}</button>
          </div>
        </>
      )}
      {c.situacao === 'analisado' && <p className="p-sup__fim"><b>{t('suporte.situacao.analisado')}.</b> {t('suporte.fimAnalisado')}</p>}
      {(c.situacao === 'recebido' || c.situacao === 'analise') && <p className="p-sup__fim">{t('suporte.fimAndando')}</p>}
    </div>
  );
}
